<template>
  <aside class="ai-prompt-panel">
    <header class="ai-prompt-panel__header">
      <h2 class="ai-prompt-panel__title">Prompt library</h2>
      <v-btn icon small aria-label="Close" @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </header>

    <div class="ai-prompt-panel__tabs" role="tablist">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        type="button"
        role="tab"
        class="ai-prompt-panel__tab"
        :class="{ 'ai-prompt-panel__tab--active': activeTab === tab.key }"
        :aria-selected="activeTab === tab.key ? 'true' : 'false'"
        @click="activeTab = tab.key"
      >
        {{ tab.label }}
      </button>
    </div>

    <template v-if="activeTab === 'prompts'">
      <div class="ai-prompt-panel__filters">
        <v-text-field
          v-model="searchInput"
          placeholder="Search prompts"
          dense
          solo
          flat
          hide-details
          prepend-inner-icon="mdi-magnify"
          clearable
          background-color="#ececf1"
        />
        <div class="ai-prompt-panel__chips">
          <button
            v-for="agent in agents"
            :key="agent"
            type="button"
            class="ai-prompt-panel__chip"
            :class="{ 'ai-prompt-panel__chip--active': selectedAgent === agent }"
            @click="selectedAgent = agent"
          >
            {{ agent === 'All' ? 'All' : `Tracey ${agent}` }}
          </button>
        </div>
      </div>
      <div class="ai-prompt-panel__list">
        <section v-for="group in groupedPrompts" :key="group.key" class="ai-prompt-panel__group">
          <h3 class="ai-prompt-panel__group-title">
            {{ group.category }}
            <span class="ai-prompt-panel__agent" :class="`ai-prompt-panel__agent--${group.agent.toLowerCase()}`">
              {{ group.agent }}
            </span>
          </h3>
          <button
            v-for="prompt in group.prompts"
            :key="prompt.id"
            type="button"
            class="ai-prompt-panel__card"
            @click="$emit('select', prompt)"
          >
            {{ prompt.prompt }}
          </button>
        </section>
        <p v-if="groupedPrompts.length === 0" class="ai-prompt-panel__empty">No prompts found</p>
      </div>
    </template>

    <div v-else class="ai-prompt-panel__list ai-prompt-panel__guide">
      <p class="ai-prompt-panel__guide-intro">
        Clear, specific prompts get you sharper answers, faster.
      </p>

      <div class="ai-prompt-panel__formula">
        <span class="ai-prompt-panel__eyebrow">The prompt formula</span>
        <strong class="ai-prompt-panel__formula-text">{{ guide.formula }}</strong>
        <span class="ai-prompt-panel__formula-example">{{ guide.formulaExample }}</span>
      </div>

      <h3 class="ai-prompt-panel__guide-heading">Which agent should I use?</h3>
      <div v-for="agent in guide.agents" :key="agent.name" class="ai-prompt-panel__agent-card">
        <strong>Choose {{ agent.name }} when</strong>
        <span>{{ agent.when }}</span>
      </div>

      <h3 class="ai-prompt-panel__guide-heading">Twelve habits for great prompts</h3>
      <details
        v-for="(habit, i) in guide.habits"
        :key="habit.title"
        class="ai-prompt-panel__habit"
      >
        <summary>
          <span class="ai-prompt-panel__habit-num">{{ i + 1 }}</span>
          {{ habit.title }}
        </summary>
        <p>{{ habit.body }}</p>
      </details>

      <h3 class="ai-prompt-panel__guide-heading">Weak prompt versus strong prompt</h3>
      <div v-for="example in guide.examples" :key="example.weak" class="ai-prompt-panel__example">
        <span class="ai-prompt-panel__example-label ai-prompt-panel__example-label--weak">Weak</span>
        <span class="ai-prompt-panel__example-weak">{{ example.weak }}</span>
        <span class="ai-prompt-panel__example-label ai-prompt-panel__example-label--strong">Strong</span>
        <button
          type="button"
          class="ai-prompt-panel__card ai-prompt-panel__card--strong"
          title="Use this prompt"
          @click="$emit('select', { prompt: example.strong })"
        >
          {{ example.strong }}
        </button>
      </div>
    </div>
  </aside>
</template>

<script>
import { PROMPT_AGENTS, PROMPT_LIBRARY, PROMPTING_GUIDE } from './promptLibrary.js';

export default {
  name: 'TraceyAiPromptPanel',
  data() {
    return {
      tabs: [
        { key: 'prompts', label: 'Prompts' },
        { key: 'guide', label: 'Prompting guide' },
      ],
      activeTab: 'prompts',
      agents: PROMPT_AGENTS,
      selectedAgent: 'All',
      searchInput: '',
      guide: PROMPTING_GUIDE,
    };
  },
  computed: {
    groupedPrompts() {
      const term = (this.searchInput || '').toLowerCase().trim();
      const groups = [];
      const byKey = {};
      PROMPT_LIBRARY.forEach((p) => {
        if (this.selectedAgent !== 'All' && p.agent !== this.selectedAgent) return;
        if (term && !p.prompt.toLowerCase().includes(term) && !p.category.toLowerCase().includes(term)) return;
        const key = `${p.agent}:${p.category}`;
        if (!byKey[key]) {
          byKey[key] = { key, agent: p.agent, category: p.category, prompts: [] };
          groups.push(byKey[key]);
        }
        byKey[key].prompts.push(p);
      });
      return groups;
    },
  },
};
</script>

<style scoped>
.ai-prompt-panel {
  display: flex;
  flex-direction: column;
  width: 320px;
  min-width: 320px;
  height: 100%;
  background: #fff;
  border-left: 1px solid #ececf1;
}

.ai-prompt-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid #ececf1;
}

.ai-prompt-panel__title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #0d0d0d;
}

.ai-prompt-panel__tabs {
  display: flex;
  gap: 4px;
  padding: 10px 16px 0;
  border-bottom: 1px solid #ececf1;
  flex-shrink: 0;
}

.ai-prompt-panel__tab {
  padding: 6px 10px 8px;
  font-size: 13px;
  font-weight: 600;
  color: #6e6e80;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
}

.ai-prompt-panel__tab--active {
  color: #12253a;
  border-bottom-color: #ff1463;
}

.ai-prompt-panel__filters {
  padding: 12px 16px;
  flex-shrink: 0;
}

.ai-prompt-panel__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}

.ai-prompt-panel__chip {
  border: 1px solid #d9d9e3;
  border-radius: 16px;
  padding: 4px 12px;
  font-size: 12px;
  background: #fff;
  color: #353740;
  cursor: pointer;
}

.ai-prompt-panel__chip--active {
  background: #12253a;
  border-color: #12253a;
  color: #fff;
}

.ai-prompt-panel__list {
  flex: 1;
  overflow-y: auto;
  padding: 4px 12px 16px;
  min-height: 0;
}

.ai-prompt-panel__group {
  margin-bottom: 14px;
}

.ai-prompt-panel__group-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: 8px 4px 6px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #12253a;
}

.ai-prompt-panel__agent {
  flex-shrink: 0;
  padding: 1px 7px;
  border-radius: 10px;
  font-size: 10px;
  letter-spacing: 0.02em;
}

.ai-prompt-panel__agent--sprint {
  background: #e6f4f8;
  color: #0e7490;
}

.ai-prompt-panel__agent--ultra {
  background: #ffe8ef;
  color: #c80f4e;
}

.ai-prompt-panel__card {
  display: block;
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  margin-bottom: 6px;
  border: 1px solid #ececf1;
  border-radius: 10px;
  background: #f7f7f8;
  font-size: 13px;
  line-height: 1.4;
  color: #0d0d0d;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}

.ai-prompt-panel__card:hover {
  background: #fff;
  border-color: #acacbe;
}

.ai-prompt-panel__empty {
  text-align: center;
  color: #8e8ea0;
  font-size: 14px;
  padding: 24px;
}

.ai-prompt-panel__guide {
  padding-top: 12px;
  font-size: 13px;
  line-height: 1.5;
  color: #353740;
}

.ai-prompt-panel__guide-intro {
  margin: 0 4px 12px;
}

.ai-prompt-panel__formula {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  border-radius: 10px;
  background: #f4f6fa;
}

.ai-prompt-panel__eyebrow {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #ff1463;
}

.ai-prompt-panel__formula-text {
  font-size: 13px;
  color: #12253a;
}

.ai-prompt-panel__formula-example {
  font-size: 12px;
  color: #6e6e80;
}

.ai-prompt-panel__guide-heading {
  margin: 18px 4px 8px;
  font-size: 13px;
  font-weight: 700;
  color: #12253a;
}

.ai-prompt-panel__agent-card {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px 12px;
  margin-bottom: 6px;
  border: 1px solid #ececf1;
  border-radius: 10px;
}

.ai-prompt-panel__agent-card strong {
  color: #12253a;
  font-size: 12px;
}

.ai-prompt-panel__habit {
  border-bottom: 1px solid #ececf1;
}

.ai-prompt-panel__habit summary {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 8px 4px;
  font-weight: 600;
  color: #12253a;
  cursor: pointer;
}

.ai-prompt-panel__habit-num {
  flex-shrink: 0;
  min-width: 16px;
  font-weight: 700;
  color: #ff1463;
}

.ai-prompt-panel__habit p {
  margin: 0 4px 10px 28px;
}

.ai-prompt-panel__example {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 10px 0;
  border-bottom: 1px solid #ececf1;
}

.ai-prompt-panel__example-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.ai-prompt-panel__example-label--weak {
  color: #8a2a22;
}

.ai-prompt-panel__example-label--strong {
  margin-top: 4px;
  color: #14532d;
}

.ai-prompt-panel__example-weak {
  color: #6e6e80;
  text-decoration: line-through;
}

.ai-prompt-panel__card--strong {
  margin-bottom: 0;
}
</style>
